import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ExternalLink, RefreshCw } from "lucide-react";
import { toast } from "sonner";

// Shown when the server blocks a booking because the linked customer/pet records miss mandatory fields.
// Profiles open in a new tab so every booking detail entered here stays exactly as it is.
export function RecordIncompletePanel({ missing, customerId, petIds, onResolved }) {
  const [busy, setBusy] = useState(false);
  if (!missing?.length) return null;
  const groups = Object.values(missing.reduce((g, m) => {
    const k = `${m.entity}:${m.id}`;
    (g[k] ||= { ...m, labels: [] }).labels.push(m.label);
    return g;
  }, {}));
  const recheck = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/bookings/record-readiness`, { params: { customer_id: customerId, pet_ids: (petIds || []).join(",") } });
      onResolved(data.missing);
      if (data.ok) toast.success("All required details are complete — you can create the booking now.");
      else toast.error("Some required details are still missing.");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="record-incomplete-panel">
      <div className="flex items-start gap-2 font-semibold"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        {missing.some((m) => m.field === "age") ? "Pet age is required before this booking can be confirmed." : "Complete the required details before this booking can be confirmed."}</div>
      <ul className="mt-2 space-y-1.5">
        {groups.map((g) => (
          <li key={`${g.entity}-${g.id}`} className="flex flex-wrap items-center justify-between gap-2" data-testid={`record-incomplete-${g.entity}-${g.id}`}>
            <span><b>{g.name}</b> <span className="text-xs text-amber-700">({g.entity}) · missing: {[...new Set(g.labels)].join(", ")}</span></span>
            <Button size="sm" variant="outline" className="h-8 gap-1 border-amber-300 bg-white text-amber-900" data-testid={`record-incomplete-open-${g.entity}-${g.id}`}
              onClick={() => window.open(g.entity === "pet" ? `/pets/${g.id}?edit=1` : `/customers/${g.id}?edit=1`, "_blank", "noopener")}>
              <ExternalLink className="h-3.5 w-3.5" /> Open {g.entity === "pet" ? "pet" : "customer"} to complete
            </Button>
          </li>
        ))}
      </ul>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-[11px] text-amber-700">Your booking details stay here. Save the record in the new tab, then check again. If your business requires owner approval for edits, check again after it is approved.</span>
        <Button size="sm" onClick={recheck} disabled={busy} data-testid="record-incomplete-recheck" className="h-8 gap-1 bg-amber-600 text-white hover:bg-amber-700">
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Check again</Button>
      </div>
    </div>
  );
}
